import { Auth, IAuthToken } from '@shared/Auth';
import { ModelService } from '@shared/models/model-service';
import { LumiDB, ThreeJsResponse } from '@lumidb/lumidb';
import { Vector3 } from '@math.gl/core';

export type LumiDBPoint = {
    point: Vector3;
    color: [number, number, number];
    sourceFileId: number;
};

type LumiDBToken = {
    token: string;
};

async function getTokenForTable(table_name: string): Promise<string> {
    const vizBaseUrl: string = ModelService.getVizApiBaseUrl();
    const token: IAuthToken = await Auth.getToken();

    const resp: Response = await fetch(`${vizBaseUrl}/lumidb/token?project_uuid=${kuura.getProjectUuid()}&table_name=${table_name}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token.accessToken}`,
        },
    });

    if (!resp.ok) {
        console.error('Failed to get LumiDB token', resp);
        return '';
    }

    const data: LumiDBToken = await resp.json();
    return data.token;
}

async function threeJsResponseToPointArray(threejsResponse: ThreeJsResponse): Promise<LumiDBPoint[]> {
    const offset = new Vector3(threejsResponse.offset);
    const positionArray: ArrayLike<number> = threejsResponse.pointGeometry.attributes.position.array;
    const points: Vector3[] = [];

    // Position array contains x, y, z values sequentially
    for (let i = 0; i < positionArray.length; i += 3) {
        const x = positionArray[i];
        const y = positionArray[i + 1];
        const z = positionArray[i + 2];

        // Create position vector and add offset to get real world coordinates
        const position = new Vector3(x, y, z).add(offset);
        points.push(position);
    }

    const colors: [number, number, number][] = [];
    const colorArray: ArrayLike<number> = threejsResponse.pointGeometry.attributes.color.array;

    for (let i = 0; i < colorArray.length; i += 3) {
        const r = colorArray[i];
        const g = colorArray[i + 1];
        const b = colorArray[i + 2];
        colors.push([r, g, b]);
    }

    const sourceFileIds: number[] = threejsResponse.pointGeometry.attributes.source_file_id.array as number[];

    const lumiDbPoints: LumiDBPoint[] = [];

    for (let i = 0; i < points.length; i++) {
        lumiDbPoints.push({
            point: points[i],
            color: colors[i],
            sourceFileId: sourceFileIds[i],
        });
    }

    return lumiDbPoints;
}

async function getPointsInsideRegion(region: number[][], lumidb: LumiDB, sourceFileFilter: number[] | null = null): Promise<LumiDBPoint[]> {
    if (!Array.isArray(region) || region.length < 3) {
        throw new Error('Region must be an array of at least 3 [x,y] points');
    }
    console.log('LumiDB instance:', lumidb.query);

    const normalized = [...region];
    const [fx, fy] = normalized[0];
    const [lx, ly] = normalized[normalized.length - 1];
    if (fx !== lx || fy !== ly) normalized.push([fx, fy]);

    const tableName = kuura.getProjectUuid();

    // Explicitly construct the polygon tuple structure
    const polygon: [number[][][], [number, number]] = [[normalized], [-100, 100]];

    console.log('Source file filter:', sourceFileFilter);
    const body = {
        tableName,
        queryBoundary: {
            Polygon: polygon,
        },
        queryProj: 'EPSG:3857',
        maxPoints: 1000,
        maxDensity: 1000.0,
        sourceFileFilter: sourceFileFilter,
        classFilter: null,
        outputType: 'threejs' as const,
    };

    const resp: ThreeJsResponse = await lumidb.query(body);

    const pointArray: LumiDBPoint[] = await threeJsResponseToPointArray(resp);
    return pointArray;
}

export const LumiDBClient = {
    getTokenForTable,
    getPointsInsideRegion,
};
